import { useEffect, useMemo, useState } from "react";
import { useAuth, UserProfile } from "@clerk/react";
import { toast } from "sonner";
import { Building2, Landmark, UserCog, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { makeApi } from "@/lib/api";
import { Section, LogoPicker, CompanyFields, RemitFields } from "@/components/settings/SettingsSections";
import { InvoiceNumbering, LoadNumbering, numberingInvalid } from "@/components/settings/InvoiceNumbering";

const NAV = [["company", "Company info", Building2], ["billing", "Remit-to & terms", Landmark], ["account", "Account", UserCog]];
const KEYS = ["name", "address", "phone", "email", "mc_number", "dot_number", "remit_direct", "remit_factored", "default_terms", "default_remit", "inv_prefix", "inv_suffix", "inv_next", "inv_pad", "ld_prefix", "ld_suffix", "ld_next", "ld_pad"];
const NUMS = ["inv_next", "inv_pad", "ld_next", "ld_pad"];
const str = (v) => (v === null || v === undefined ? "" : String(v));

export default function Settings() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then(({ data: d }) => { setData(d); setF(Object.fromEntries(KEYS.map((k) => [k, str(d[k])]))); })
      .catch(() => toast.error("Could not load settings"));
  }, [api]);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const dirty = f && data && KEYS.some((k) => str(data[k]) !== f[k]);
  const invalid = f && numberingInvalid(f);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, ...Object.fromEntries(NUMS.map((k) => [k, parseInt(f[k], 10) || (k.endsWith("_pad") ? 0 : null)])) };
      // only move a counter when the user changed it
      if (f.inv_next === str(data.inv_next)) body.inv_next = null;
      if (f.ld_next === str(data.ld_next)) body.ld_next = null;
      const { data: d } = await api.put("/settings/company", body);
      setData(d);
      setF(Object.fromEntries(KEYS.map((k) => [k, str(d[k])])));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="settings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Your company details appear on every invoice you send.</p>
        </div>
        <Button onClick={save} disabled={!dirty || saving || invalid} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="settings-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{dirty ? "Save changes" : "Saved"}
        </Button>
      </div>
      <div className="mt-8 grid lg:grid-cols-[200px_1fr] gap-8">
        <nav className="hidden lg:block sticky top-24 self-start space-y-1" data-testid="settings-nav">
          {NAV.map(([id, label, Icon]) => (
            <a key={id} href={`#${id}`} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900" data-testid={`settings-nav-${id}`}>
              <Icon className="w-4 h-4" /> {label}
            </a>
          ))}
        </nav>
        <div className="space-y-6 min-w-0">
          {!f ? <div className="text-slate-500">Loading…</div> : (
            <>
              <Section id="company" title="Company info" desc="Shown as the sender at the top of your invoices.">
                <LogoPicker api={api} hasLogo={data.has_logo} onChanged={(v) => setData((d) => ({ ...d, has_logo: v }))} />
                <div className="mt-6"><CompanyFields f={f} set={set} /></div>
                <InvoiceNumbering f={f} set={set} />
                <LoadNumbering f={f} set={set} />
              </Section>
              <Section id="billing" title="Remit-to & payment terms" desc="Keep two remit-to profiles and pick one per invoice or batch.">
                <RemitFields f={f} set={set} terms={data.terms_options} />
              </Section>
            </>
          )}
          <Section id="account" title="Account" desc="Your sign-in email, password and security.">
            <div className="overflow-x-auto -mx-2" data-testid="settings-account">
              <UserProfile routing="hash" appearance={{ elements: { rootBox: "w-full", cardBox: "w-full shadow-none border border-slate-200 rounded-xl" } }} />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
